/**
 * The `artifact-suggestions` card on web (plan 7.4): what `updatePage` or
 * `updateDocument` suggested in suggest mode. Each suggestion shows why,
 * the words now and the suggested words, with Apply (one manual op, no
 * model call) and Dismiss; Apply all sends every pending one in one batch.
 * Open to review opens the file in its editor, where the same suggestions
 * sit beside the blocks they target. `artifact-suggestions-card.native.tsx`
 * is the native twin.
 */
import { useEffect, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { SpinnerGlyph } from '../../common/SpinnerGlyph';
import { openArtifactPanelEditing } from '../../artifacts/artifact-panel-store';
import { parseSuggestionsResult } from '../../artifacts/suggestions/suggestions-model';
import {
  registerSuggestionSet,
  useSuggestionSet,
  type SuggestionItem,
} from '../../artifacts/suggestions/suggestions-store';
import { applySuggestions, dismissSuggestion } from '../../artifacts/suggestions/suggestions-controller';
import { useSuggestionsDeps } from '../../artifacts/suggestions/useSuggestionsDeps';
import { useIsArtifactInEditor } from '../../artifacts/editor/active-editor';
import type { ToolCardProps } from './types';

const STATUS_KEYS: Partial<Record<SuggestionItem['status'], string>> = {
  applied: 'chat.artifacts.suggestions.applied',
  dismissed: 'chat.artifacts.suggestions.dismissed',
  stale: 'chat.artifacts.suggestions.stale',
  unsupported: 'chat.artifacts.suggestions.notApplicable',
  failed: 'chat.artifacts.suggestions.failed',
  applying: 'chat.artifacts.suggestions.applying',
};

export const ArtifactSuggestionsCard: FC<ToolCardProps> = ({ toolCallId, result }) => {
  const { t } = useTranslation('common');
  const parsed = useMemo(() => parseSuggestionsResult(result), [result]);
  useEffect(() => {
    if (parsed) registerSuggestionSet(toolCallId, parsed);
  }, [parsed, toolCallId]);
  const set = useSuggestionSet(toolCallId);
  const deps = useSuggestionsDeps();
  const inEditor = useIsArtifactInEditor(parsed?.artifactId);
  const [busy, setBusy] = useState(false);
  if (!parsed) return null;
  const items = set?.items ?? [];
  const pending = items.filter((item) => item.status === 'pending' || item.status === 'failed');
  const title = parsed.title || t(`chat.artifacts.kind.${parsed.kind}`);

  const apply = async (indexes: number[] | 'all') => {
    setBusy(true);
    try {
      await applySuggestions(toolCallId, indexes, deps);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="tabbio-suggestions-card" aria-label={t('chat.artifacts.suggestions.title')}>
      <header className="tabbio-suggestions-head">
        <MaterialSymbol name="lightbulb" size={20} className="tabbio-canvas-card-glyph" />
        <div className="tabbio-suggestions-head-text">
          <div className="tabbio-canvas-card-title">{t('chat.artifacts.suggestions.title')}</div>
          <div className="tabbio-canvas-card-meta" dir="auto">
            {items.length
              ? t('chat.artifacts.suggestions.count', { count: items.length, title })
              : t('chat.artifacts.suggestions.none')}
          </div>
        </div>
        {parsed.kind === 'page' || parsed.kind === 'document' || parsed.kind === 'slides' ? (
          <button
            type="button"
            className="tabbio-text-button"
            onClick={() => openArtifactPanelEditing(parsed.artifactId)}
            disabled={inEditor}
          >
            {t('chat.artifacts.suggestions.open')}
          </button>
        ) : null}
      </header>
      {items.length ? (
        <ol className="tabbio-suggestions-list">
          {items.map((item) => {
            const settled = item.status !== 'pending' && item.status !== 'failed';
            const statusKey = STATUS_KEYS[item.status];
            return (
              <li key={item.index} className={`tabbio-suggestion-item is-${item.status}`}>
                {item.reason ? (
                  <p className="tabbio-suggestion-reason" dir="auto">
                    {item.reason}
                  </p>
                ) : null}
                <div className="tabbio-suggestion-diff">
                  {item.before ? (
                    <div className="tabbio-suggestion-before">
                      <span>{t('chat.artifacts.suggestions.before')}</span>
                      <p dir="auto">{item.before}</p>
                    </div>
                  ) : null}
                  <div className="tabbio-suggestion-after">
                    <span>{t('chat.artifacts.suggestions.after')}</span>
                    <p dir="auto">{item.after}</p>
                  </div>
                </div>
                <div className="tabbio-suggestion-actions">
                  {statusKey ? (
                    <span className="tabbio-suggestion-status" role="status">
                      {item.status === 'applying' ? <SpinnerGlyph color="var(--text-secondary)" size={14} /> : null}
                      {item.status === 'applied' ? <MaterialSymbol name="check" size={16} /> : null}
                      {t(statusKey)}
                    </span>
                  ) : null}
                  {!settled ? (
                    <>
                      <button
                        type="button"
                        className="tabbio-text-button"
                        onClick={() => dismissSuggestion(toolCallId, item.index)}
                        disabled={busy}
                      >
                        {t('chat.artifacts.suggestions.dismiss')}
                      </button>
                      <button
                        type="button"
                        className="tabbio-panel-primary is-small"
                        onClick={() => void apply([item.index])}
                        disabled={busy}
                      >
                        {t('chat.artifacts.suggestions.apply')}
                      </button>
                    </>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      ) : null}
      {pending.length > 1 ? (
        <footer className="tabbio-suggestions-foot">
          <button type="button" className="tabbio-panel-primary is-small" onClick={() => void apply('all')} disabled={busy}>
            {t('chat.artifacts.suggestions.applyAll')}
          </button>
        </footer>
      ) : null}
    </section>
  );
};
